import { useState } from 'react'
import { useTranslation } from 'react-i18next'

const LLMConfigPanel = ({ config, onChange }) => {
  const { t } = useTranslation()
  const [showAdvanced, setShowAdvanced] = useState(false)

  const handleChange = (field, value) => {
    onChange({ ...config, [field]: value })
  }

  const providers = [
    { value: 'openai/gpt-4o', label: 'OpenAI GPT-4o (Best Quality)' },
    { value: 'openai/gpt-4o-mini', label: 'OpenAI GPT-4o-mini (Fast & Cheap)' },
    { value: 'openai/gpt-3.5-turbo', label: 'OpenAI GPT-3.5 Turbo' },
    { value: 'anthropic/claude-3-5-sonnet-20241022', label: 'Anthropic Claude 3.5 Sonnet' },
    { value: 'anthropic/claude-3-haiku-20240307', label: 'Anthropic Claude 3 Haiku (Fast)' },
    { value: 'inference-net/schematron-8b', label: 'Inference.net Schematron 8B (OpenAI-compatible)' },
    { value: 'inference-net/schematron-3b', label: 'Inference.net Schematron 3B (OpenAI-compatible)' },
    { value: 'ollama/llama3.2', label: 'Ollama Llama 3.2 (Local, Free)' },
    { value: 'ollama/mistral', label: 'Ollama Mistral (Local, Free)' },
  ]

  return (
    <div className="bg-white rounded-xl shadow-sm border border-light-200/20 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-bold text-dark-300 flex items-center">
          <i className="fa-solid fa-robot text-primary-500 mr-2"></i>
          {t('llmConfig.title')}
        </h3>
        <div className="flex items-center space-x-2">
          <span className="text-sm font-semibold text-primary-500">
            {config.enabled ? t('llmConfig.active') : t('llmConfig.disabled')}
          </span>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={config.enabled}
              onChange={(e) => handleChange('enabled', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-primary-800 peer-checked:to-primary-500"></div>
          </label>
        </div>
      </div>

      {config.enabled && (
        <div className="space-y-6">
          {/* Provider Selection */}
          <div>
            <label className="block mb-2">
              <span className="text-sm font-bold text-dark-300">{t('llmConfig.provider')} <span className="text-primary-500">*</span></span>
            </label>
            <div className="relative">
              <select
                value={config.provider}
                onChange={(e) => handleChange('provider', e.target.value)}
                className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300 appearance-none bg-white"
              >
                {providers.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
              <i className="fa-solid fa-chevron-down absolute right-4 top-4 text-light-200 pointer-events-none"></i>
            </div>
            <p className="text-xs text-dark-100 mt-2">
              {config.provider.startsWith('openai') && !config.provider.startsWith('openai/inference-net') && '~$0.001-0.002 per extraction'}
              {config.provider.startsWith('anthropic') && '~$0.002-0.005 per extraction'}
              {config.provider.startsWith('inference-net') && 'Inference.net Schematron (set INFERENCE_API_KEY in .llm.env)'}
              {config.provider.startsWith('ollama') && '🆓 Free (requires local Ollama)'}
            </p>
          </div>

          {/* API Key (Optional) */}
          {!config.provider.startsWith('ollama') && (
            <div>
              <label className="block mb-2">
                <span className="text-sm font-bold text-dark-300">{t('llmConfig.apiKey')}</span>
              </label>
              <input
                type="password"
                value={config.api_key}
                onChange={(e) => handleChange('api_key', e.target.value)}
                placeholder="sk-..."
                className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300 placeholder-light-200"
              />
              <p className="text-xs text-dark-100 mt-2">
                {t('llmConfig.apiKeyDesc')}
              </p>
              <div className="bg-light-100 border-l-4 border-light-200 rounded-r-lg p-3 mt-3">
                <p className="text-xs text-dark-100">
                  <i className="fa-solid fa-info-circle mr-1"></i>
                  {t('llmConfig.apiKeyInfo')}
                </p>
              </div>
            </div>
          )}

          {/* Instruction/Prompt */}
          <div>
            <label className="block mb-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-bold text-dark-300">{t('llmConfig.instruction')} <span className="text-primary-500">*</span></span>
                <button 
                  type="button"
                  className="text-xs bg-light-100 hover:bg-light-200/20 px-3 py-1 rounded-lg font-semibold text-dark-300 transition-colors"
                >
                  <i className="fa-solid fa-wand-magic-sparkles mr-1"></i>
                  {t('llmConfig.aiOptimize')}
                </button>
              </div>
              <textarea
                value={config.instruction}
                onChange={(e) => handleChange('instruction', e.target.value)}
                rows={8}
                className="w-full px-4 py-3 border-2 border-light-200/30 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-dark-300 font-mono text-sm"
                placeholder={t('llmConfig.instructionPlaceholder')}
              />
            </label>
            
            <div className="bg-primary-500/5 border-l-4 border-primary-500 rounded-r-lg p-4 mt-4">
              <p className="text-sm text-dark-100">
                <i className="fa-solid fa-lightbulb mr-2"></i>
                <strong>{t('llmConfig.tipTitle')}</strong> {t('llmConfig.tipExample')}
              </p>
            </div>
          </div>

          {/* Cache */}
          <div className="flex items-center justify-between pt-4 border-t border-light-200/20">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="cache-results"
                checked={config.cache}
                onChange={(e) => handleChange('cache', e.target.checked)}
                className="w-4 h-4 text-primary-500 border-light-200/30 rounded focus:ring-primary-500"
              />
              <label htmlFor="cache-results" className="text-sm font-semibold text-dark-300">
                {t('llmConfig.cache')}
              </label>
            </div>
            <span className="text-xs text-dark-100">{t('llmConfig.cacheDesc')}</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default LLMConfigPanel
